/**
 * M-03 月次処理。事前確認 → 試算 → 差分確認 → 確定の4段階を進める。
 *
 * 確定条件を満たすまで確定ボタンは押せない。金額は管理部のみが見られる。
 */

import { useState } from 'react';
import { endpoints } from '../../api/endpoints.js';
import type { MonthlyCalculateResponse } from '../../api/types.js';
import { MonthNav } from '../../components/MonthNav.js';
import { Card, ErrorMessage, Loading, Notice, ProvisionalBadge } from '../../components/ui.js';
import {
  currentMonth,
  formatDateTime,
  formatDuration,
  formatMonthLabel,
  formatYen,
} from '../../lib/format.js';
import { useAsync } from '../../lib/useAsync.js';

export function MonthlyPage(): React.JSX.Element {
  const [month, setMonth] = useState(currentMonth());
  const state = useAsync(() => endpoints.monthlyState(month), [month]);

  const [calculation, setCalculation] = useState<MonthlyCalculateResponse | null>(null);
  const [busy, setBusy] = useState<'calculate' | 'close' | 'reopen' | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [reopenReason, setReopenReason] = useState('');

  const run = async (
    kind: 'calculate' | 'close' | 'reopen',
    action: () => Promise<void>,
  ): Promise<void> => {
    setBusy(kind);
    setActionError(null);
    setMessage(null);
    try {
      await action();
      state.reload();
    } catch (cause) {
      setActionError(cause);
    } finally {
      setBusy(null);
    }
  };

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorMessage error={state.error} />;
  if (!state.data) return <Loading />;

  const { conditions, canClose, closing, fiscalYear, provisional, provisionalRules, summaries } =
    state.data;
  const closed = closing?.status === 'closed';

  return (
    <>
      <MonthNav month={month} onChange={setMonth} />

      <h1 className="page-heading">{formatMonthLabel(month)}の月次処理</h1>

      {provisional && (
        <Notice tone="warn" title="正式決定前の制度項目があります">
          年間所定労働日数（{fiscalYear.annualWorkingDays}日
          {fiscalYear.approved ? '・承認済み' : '・未承認'}
          ）など未確定の設定値を含むため、結果は試算として扱います。
          {provisionalRules.length > 0 && (
            <ul className="mt-1 list-disc pl-5">
              {provisionalRules.map((rule) => (
                <li key={rule.id}>
                  {rule.name}（{rule.source}）
                </li>
              ))}
            </ul>
          )}
        </Notice>
      )}

      <ErrorMessage error={actionError} />
      {message !== null && <Notice tone="info">{message}</Notice>}

      <Card title="① 確定条件">
        <ul className="divide-y divide-line text-sm">
          {conditions.map((condition) => (
            <li key={condition.code} className="flex items-center justify-between gap-3 py-2">
              <div>
                <p className="font-semibold">{condition.label}</p>
                <p className="text-[0.78rem] text-ink-muted">{condition.detail}</p>
              </div>
              <span className={`pill ${condition.satisfied ? 'pill--ok' : 'pill--warn'}`}>
                {condition.satisfied ? '完了' : '未完了'}
              </span>
            </li>
          ))}
        </ul>
      </Card>

      <Card
        title="② 月次試算"
        description={
          closing?.calculatedAt
            ? `${formatDateTime(closing.calculatedAt)} に ${closing.calculatedByName ?? '管理部'} が計算版 ${closing.engineVersion} で試算しました`
            : 'まだ試算していません'
        }
        action={
          <button
            type="button"
            className="btn btn--primary btn--sm"
            disabled={busy !== null || closed}
            onClick={() =>
              void run('calculate', async () => {
                const result = await endpoints.calculateMonth(month);
                setCalculation(result);
                setMessage(
                  `最新の入力と計算版 ${result.engineVersion} で試算しました（${result.employees.length}名）。`,
                );
              })
            }
          >
            {closing?.calculatedAt ? '再試算する' : '試算する'}
          </button>
        }
      >
        {summaries.length === 0 ? (
          <p className="text-sm text-ink-muted">試算するとスタッフごとの集計が表示されます。</p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>社員ID</th>
                  <th>氏名</th>
                  <th className="text-right">出勤</th>
                  <th className="text-right">実働</th>
                  <th className="text-right">移動</th>
                  <th className="text-right">稼働</th>
                  <th className="text-right">月残業</th>
                  <th className="text-right">深夜</th>
                  <th className="text-right">食事手当</th>
                </tr>
              </thead>
              <tbody>
                {summaries.map((summary) => (
                  <tr key={summary.employeeId}>
                    <td>{summary.employeeNumber}</td>
                    <td className="font-semibold">{summary.employeeName}</td>
                    <td className="text-right">{summary.attendanceDays}日</td>
                    <td className="text-right">{summary.display.workHours}</td>
                    <td className="text-right">{formatDuration(summary.travelMinutes)}</td>
                    <td className="text-right">{summary.display.totalHours}</td>
                    <td className="text-right">{summary.display.monthlyOvertimeHours}</td>
                    <td className="text-right">{summary.display.nightHours}</td>
                    <td className="text-right">{summary.mealAllowanceCount}回</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {calculation && (
        <Card
          title={
            <span className="flex items-center gap-2">
              ③ 差分確認 {provisional && <ProvisionalBadge />}
            </span>
          }
          description="前回の試算からの変化です。"
        >
          {calculation.diffs.length === 0 ? (
            <p className="text-sm">前回の試算から変化はありません。</p>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>氏名</th>
                  <th>項目</th>
                  <th className="text-right">前回</th>
                  <th className="text-right">今回</th>
                </tr>
              </thead>
              <tbody>
                {calculation.diffs.map((diff, index) => (
                  <tr key={`${diff.employeeId}-${diff.field}-${index}`}>
                    <td>{diff.employeeName}</td>
                    <td>{diff.label}</td>
                    <td className="text-right">{formatDuration(diff.before)}</td>
                    <td className="text-right font-semibold">{formatDuration(diff.after)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {calculation && (
        <Card title="給与項目（管理部のみ）" description="給与マスタの金額をもとにした試算です。">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>氏名</th>
                  <th className="text-right">時間給単価</th>
                  <th className="text-right">残業手当</th>
                  <th className="text-right">深夜手当</th>
                  <th className="text-right">休日手当</th>
                  <th className="text-right">食事手当</th>
                  <th className="text-right">固定残業差額</th>
                  <th className="text-right">手当合計</th>
                </tr>
              </thead>
              <tbody>
                {calculation.employees.map((item) => (
                  <tr key={item.employeeId}>
                    <td className="font-semibold">{item.employeeName}</td>
                    <td className="text-right">{formatYen(item.payroll?.hourlyRate ?? 0)}</td>
                    <td className="text-right">
                      {formatYen(
                        (item.payroll?.overtimePay ?? 0) + (item.payroll?.overtimeOver60Pay ?? 0),
                      )}
                    </td>
                    <td className="text-right">{formatYen(item.payroll?.nightPay ?? 0)}</td>
                    <td className="text-right">
                      {formatYen(
                        (item.payroll?.legalHolidayPay ?? 0) +
                          (item.payroll?.prescribedHolidayPay ?? 0),
                      )}
                    </td>
                    <td className="text-right">{formatYen(item.payroll?.mealAllowancePay ?? 0)}</td>
                    <td className="text-right">
                      {formatYen(item.payroll?.fixedOvertimeDifference ?? 0)}
                    </td>
                    <td className="text-right font-semibold">
                      {formatYen(item.payroll?.totalAllowance ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card
        title="④ 月次確定"
        description="確定すると日報は編集できなくなり、確定時点の内容を保存します。"
      >
        {closed ? (
          <div className="space-y-3">
            <Notice tone="info" title="確定済みです">
              {formatDateTime(closing?.closedAt ?? null)} に {closing?.closedByName ?? '管理部'}{' '}
              が確定しました。確定時点の日報・集計・給与単価を保存しています。
            </Notice>
            <label className="block">
              <span className="field-label">確定を取り消す理由（必須）</span>
              <input
                className="input"
                type="text"
                value={reopenReason}
                placeholder="例：入力漏れが判明したため"
                onChange={(event) => setReopenReason(event.target.value)}
              />
            </label>
            <button
              type="button"
              className="btn btn--danger"
              disabled={busy !== null || reopenReason.trim() === ''}
              onClick={() =>
                void run('reopen', async () => {
                  await endpoints.reopenMonth(month, reopenReason);
                  setReopenReason('');
                  setMessage('確定を取り消しました。監査ログに記録しています。');
                })
              }
            >
              確定を取り消す
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {!canClose && (
              <Notice tone="warn" title="確定条件を満たしていません">
                <ul className="list-disc pl-5">
                  {conditions
                    .filter((condition) => !condition.satisfied)
                    .map((condition) => (
                      <li key={condition.code}>
                        {condition.label}：{condition.detail}
                      </li>
                    ))}
                </ul>
              </Notice>
            )}
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy !== null || !canClose}
              onClick={() =>
                void run('close', async () => {
                  const result = await endpoints.closeMonth(month);
                  setMessage(`確定しました（${result.snapshots}名分を保存）。`);
                })
              }
            >
              {canClose ? '月次確定する' : 'ロック中'}
            </button>
          </div>
        )}
      </Card>
    </>
  );
}
